import '@/plugins/input-mask/style/style.css'
import 'uno.css'
import { createPinia, setActivePinia } from 'pinia'
import '@/css/tailwind.css'
import SplashScreen from '@/views/SplashScreen.vue'
import { createApp } from 'vue'

if (import.meta.env.DEV) {
  const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (faviconLink) {
    faviconLink.href = '/img/favicon-32x32-dev.png'
  }
}

const appNode = '#app'
const loadingNode = '#loading'
const routerReadyCapMs = 2000

// The app mounts under the splash, which then fades out over it: no white gap between the two.
const mountApp = async () => {
  const loadingApp = createApp(SplashScreen)
  const splash = loadingApp.mount(loadingNode) as InstanceType<typeof SplashScreen>

  // Modules under mainApp, such as the router guards, call stores as they load.
  const pinia = createPinia()
  setActivePinia(pinia)

  const mainApp = await import('./mainApp')
  const mainAppInstance = await mainApp.createApp(pinia)
  mainAppInstance.mount(appNode)

  // Fade onto the resolved first page, but never hold the splash on a navigation a guard leaves pending.
  const { getRouter } = await import('@/router/router')
  await Promise.race([
    getRouter().isReady().catch(() => undefined),
    new Promise(resolve => setTimeout(resolve, routerReadyCapMs))
  ])

  await splash.beforeUnload()
  loadingApp.unmount()
}

// Migration: unregister old Workbox service workers from before #1229 and drop
// the precache they left behind (otherwise the old app shell can keep being
// served from CacheStorage, and the stale caches waste storage quota).
// Safe to remove after all users have loaded the new version (after ~2027-09).
//
// CacheStorage is origin-global and the old app has NO chunk-error recovery, so
// the precache must NOT be wiped while another tab might still be running the old
// app off it (e.g. this tab was hard-refreshed past the old SW). Deleting is
// deferred to when no SW registration remains: either a later load of this build
// (the unregister below has taken effect), or the tombstone app-sw.js activating
// (which only happens once no old-app client remains).
// CAUTION: the delete clears ALL CacheStorage — safe only while the app itself
// uses no CacheStorage. If you add legitimate cache use, scope this delete (and
// the one in public/app-sw.js) first.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    if (registrations.length > 0) {
      // An old SW (or its tombstone) is still registered: unregister so future
      // loads come from the network, but leave CacheStorage alone for now.
      registrations.forEach(r => r.unregister())
    } else if ('caches' in window) {
      caches.keys().then(keys => keys.forEach(key => caches.delete(key)))
    }
  })
}

mountApp()
